import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const chainSteps = [
  { label: '课堂原话', icon: 'ri-mic-line', color: 'primary' },
  { label: '核心概念', icon: 'ri-lightbulb-line', color: 'accent' },
  { label: '问题链', icon: 'ri-question-answer-line', color: 'primary' },
  { label: '案例链', icon: 'ri-stack-line', color: 'accent' },
  { label: '理论链', icon: 'ri-link-m', color: 'primary' },
  { label: '价值链', icon: 'ri-heart-line', color: 'accent' },
];

export default function P28TextUnderstandingNew({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden">
      {/* 三层背景光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-200/30 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-[400px] h-[400px] rounded-full bg-accent-200/25 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div className="absolute top-1/2 left-1/3 w-[300px] h-[300px] rounded-full bg-secondary-200/20 blur-[140px] pointer-events-none animate-orb-float-3" />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-8 md:py-10">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <div className="flex items-center justify-center gap-2 mb-2">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-base font-body font-semibold">
              <i className="ri-scan-line text-base" /> 技术点二
            </span>
          </div>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            课堂文字理解与<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">互动链识别</span>
          </h2>
        </div>

        {/* 流程链 */}
        <div className={`max-w-5xl mx-auto mb-6 transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
            {chainSteps.map((step, i) => (
              <div key={step.label} className="flex items-center gap-0">
                <div className={`flex items-center gap-2 px-3 py-2 rounded-xl ${step.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/30'} transition-all duration-300 hover:scale-105`}>
                  <i className={`${step.icon} text-lg ${step.color === 'primary' ? 'text-primary-500' : 'text-accent-500'}`} />
                  <span className="text-xl font-body font-semibold text-foreground-950 whitespace-nowrap">{step.label}</span>
                </div>
                {i < chainSteps.length - 1 && (
                  <i className="ri-arrow-right-s-line text-foreground-950 mx-0.5 text-lg" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* ===== 横向技术流程图 ===== */}
        <div className={`max-w-6xl mx-auto transition-all duration-1000 delay-400 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-col lg:flex-row items-stretch gap-0">
            {/* ---- 列1: 输入层 ---- */}
            <div className="flex-1 flex flex-col items-center justify-center p-3 lg:p-5">
              <div className="w-full max-w-[280px] p-7 rounded-2xl bg-background-50/40 backdrop-blur-md border border-background-200/50 text-center transition-all duration-300 hover:shadow-lg hover:border-primary-300/40 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06),inset_0_1px_1px_0_rgba(255,255,255,0.5)]">
                <div className="w-16 h-16 mx-auto mb-3 flex items-center justify-center rounded-2xl bg-primary-500">
                  <i className="ri-file-text-line text-3xl text-background-50" />
                </div>
                <h4 className="text-3xl font-body font-bold text-foreground-950 mb-1">课堂原始文字</h4>
                <p className="text-lg text-foreground-950 font-body leading-relaxed">转录文本 / 板书内容 / 互动记录</p>
              </div>
            </div>

            {/* 箭头 */}
            <div className="flex lg:flex-col items-center justify-center py-2 lg:py-0 px-0 lg:px-2">
              <div className="hidden lg:flex flex-col items-center gap-1">
                <div className="w-10 h-px bg-primary-300/60" />
                <i className="ri-arrow-right-line text-primary-400 text-xl" />
              </div>
              <div className="flex lg:hidden flex-row items-center gap-1">
                <div className="w-px h-6 bg-primary-300/60" />
                <i className="ri-arrow-down-line text-primary-400 text-lg" />
              </div>
            </div>

            {/* ---- 列2: NLP处理层 ---- */}
            <div className="flex-1 flex flex-col items-center justify-center p-3 lg:p-5">
              <div className="w-full max-w-[280px] space-y-3">
                {[
                  { icon: 'ri-scissors-line', title: '智能分词', desc: '课堂话语语义切分', bg: 'bg-primary-500', iconColor: 'text-background-50' },
                  { icon: 'ri-brain-line', title: '语义解析', desc: '概念间逻辑关系', bg: 'bg-accent-500', iconColor: 'text-background-50' },
                  { icon: 'ri-file-search-line', title: '关键信息提取', desc: '识别问题与案例', bg: 'bg-primary-500', iconColor: 'text-background-50' },
                ].map((step) => (
                  <div key={step.title} className="flex items-center gap-3 p-4 rounded-xl bg-background-50/40 backdrop-blur-md border border-background-200/50 transition-all duration-300 hover:shadow-md hover:border-primary-300/30 shadow-[0_4px_20px_-4px_rgba(0,0,0,0.06)]">
                    <div className={`w-11 h-11 flex items-center justify-center rounded-xl ${step.bg} flex-shrink-0`}>
                      <i className={`${step.icon} ${step.iconColor} text-lg`} />
                    </div>
                    <div className="text-left">
                      <h4 className="text-2xl font-body font-bold text-foreground-950">{step.title}</h4>
                      <p className="text-lg text-foreground-950 font-body leading-relaxed">{step.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 箭头 */}
            <div className="flex lg:flex-col items-center justify-center py-2 lg:py-0 px-0 lg:px-2">
              <div className="hidden lg:flex flex-col items-center gap-1">
                <div className="w-10 h-px bg-accent-300/60" />
                <i className="ri-arrow-right-line text-accent-400 text-xl" />
              </div>
              <div className="flex lg:hidden flex-row items-center gap-1">
                <div className="w-px h-6 bg-accent-300/60" />
                <i className="ri-arrow-down-line text-accent-400 text-lg" />
              </div>
            </div>

            {/* ---- 列3: 链识别层 ---- */}
            <div className="flex-1 flex flex-col items-center justify-center p-3 lg:p-5">
              <div className="w-full max-w-[280px] space-y-2">
                {[
                  { label: '问题链', icon: 'ri-question-answer-line', color: 'primary', desc: '师生问答结构' },
                  { label: '案例链', icon: 'ri-stack-line', color: 'accent', desc: '案例引用网络' },
                  { label: '理论链', icon: 'ri-link-m', color: 'primary', desc: '理论展开路径' },
                  { label: '价值链', icon: 'ri-heart-line', color: 'accent', desc: '价值回扣逻辑' },
                ].map((chain) => (
                  <div key={chain.label} className={`flex items-center gap-3 p-4 rounded-xl transition-all duration-300 hover:shadow-md hover:scale-[1.02] ${chain.color === 'primary' ? 'bg-primary-50 border border-primary-200/50' : 'bg-accent-50 border border-accent-200/50'}`}>
                    <div className={`w-10 h-10 flex items-center justify-center rounded-lg flex-shrink-0 ${chain.color === 'primary' ? 'bg-primary-500' : 'bg-accent-500'}`}>
                      <i className={`${chain.icon} text-base text-background-50`} />
                    </div>
                    <div className="text-left">
                      <h4 className="text-2xl font-body font-bold text-foreground-950">{chain.label}</h4>
                      <p className="text-lg text-foreground-950 font-body">{chain.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}